import { Route } from '@angular/router';
import { LayoutComponent } from 'app/layout/layout.component';
import { initialDataResolver } from './app.resolvers';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { InviteOnlyGuard } from './core/auth/guards/invite-only.guard';
import { NoAuthGuard } from './core/auth/guards/noAuth.guard';
import { ProfileCompletionGuard } from './core/auth/guards/profile-completion.guard';

// @formatter:off

export const appRoutes: Route[] = [
    // Service unavailable — shown when tenant config fails to load (server offline).
    // No auth guard, no layout wrapper — must render independently of all services.
    {
        path: 'service-unavailable',
        loadComponent: () =>
            import(
                'app/shared/components/pages/service-unavailable/service-unavailable.component'
            ).then((m) => m.ServiceUnavailableComponent),
    },

    // Unauthorized (403) — shown when user has no permissions for the requested page.
    // No layout wrapper — sidebar must not be visible on this page.
    {
        path: 'admin/unauthorized',
        loadComponent: () =>
            import(
                'app/modules/admin/unauthorized/unauthorized.component'
            ).then((m) => m.UnauthorizedComponent),
    },

    // Redirect empty path based on user role
    {
        path: '',
        pathMatch: 'full',
        canActivate: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import(
                        'app/shared/components/pages/role-redirect/role-redirect.component'
                    ).then((m) => m.RoleRedirectComponent),
            },
        ],
    },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    // { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'box-office' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.routes'
                    ),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'admin/create-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/create-password/create-password.routes'
                    ),
            },
            {
                path: 'admin/sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'admin/set-password/:token',
                loadComponent: () =>
                    import(
                        'app/modules/admin/set-password/set-password.component'
                    ).then((m) => m.AdminSetPasswordComponent),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import(
                        'app/modules/auth/filmmaker-sign-in/filmmaker-sign-in.routes'
                    ),
            },
            {
                path: 'sign-up',
                canActivate: [InviteOnlyGuard],
                loadChildren: () =>
                    import(
                        'app/modules/auth/filmmaker-signup/filmmaker-signup.routes'
                    ),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'profile-completion',
                loadChildren: () =>
                    import(
                        'app/modules/auth/filmmaker-signup/filmmaker-signup.routes'
                    ),
            },
            {
                // Universal onboarding entry point.
                // OnboardingShellComponent reads onboarding.flow and
                // lazy-loads the correct tenant plugin component.
                // Gate: onboarding.profileCompletionRequired + profileStatus.
                path: 'onboarding',
                loadChildren: () =>
                    import('app/modules/auth/onboarding/onboarding.routes'),
            },
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.routes'
                    ),
            },
            {
                path: 'create-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/create-password/create-password.routes'
                    ),
            },
            {
                path: 'update-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/update-password/update-password.routes'
                    ),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.routes'),
            },
        ],
    },

    // Invite route — public entry point for KFCC producer invite links.
    // No AuthGuard, no NoAuthGuard — the token IS the entry credential.
    // InviteRedirectComponent validates the token, stores user data in
    // InviteService, then redirects to /sign-in for OTP authentication.
    {
        path: 'invite/:token',
        component: LayoutComponent,
        data: { layout: 'empty' },
        loadChildren: () => import('app/modules/auth/invite/invite.routes'),
    },

    // Public routes (accessible without authentication)
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'terms-conditions',
                loadComponent: () =>
                    import('app/shared/components/pages/terms-conditions').then(
                        (m) => m.TermsConditionsComponent
                    ),
            },
            {
                path: 'privacy-policy',
                loadComponent: () =>
                    import('app/shared/components/pages/privacy-policy').then(
                        (m) => m.PrivacyPolicyComponent
                    ),
            },
            {
                path: 'payment-refund-policy',
                loadComponent: () =>
                    import(
                        'app/shared/components/pages/payment-refund-policy'
                    ).then((m) => m.PaymentRefundPolicyComponent),
            },
            {
                path: 'coming-soon',
                loadComponent: () =>
                    import(
                        'app/shared/components/pages/feature-coming-soon/feature-coming-soon.component'
                    ).then((m) => m.FeatureComingSoonComponent),
            },
            {
                path: 'film-maker/contest-registration',
                loadChildren: () =>
                    import(
                        'app/modules/public/contest-registration/contest-registration.routes'
                    ),
            },
        ],
    },

    // Film maker routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'film-maker',
                canActivate: [ProfileCompletionGuard],
                canActivateChild: [ProfileCompletionGuard],
                loadChildren: () =>
                    import('app/modules/film-maker/film-maker.routes'),
            },
            {
                path: 'user',
                canActivate: [ProfileCompletionGuard],
                canActivateChild: [ProfileCompletionGuard],
                loadChildren: () => import('app/modules/user/user.routes'),
            },
            {
                path: 'profile',
                canActivate: [ProfileCompletionGuard],
                canActivateChild: [ProfileCompletionGuard],
                loadChildren: () => import('app/modules/user/user.routes'),
            },
            {
                path: 'coming-soon',
                loadComponent: () =>
                    import(
                        'app/shared/components/pages/feature-coming-soon/feature-coming-soon.component'
                    ).then((m) => m.FeatureComingSoonComponent),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'admin',
                loadChildren: () => import('app/modules/admin/admin.routes'),
            },
        ],
    },

    // 404 Not Found route - must be the last route
    {
        path: '**',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import(
                        'app/shared/components/pages/not-found/not-found.component'
                    ).then((m) => m.NotFoundComponent),
            },
        ],
    },
];
